/* eslint-disable camelcase */
/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Loader } from 'semantic-ui-react';
import { debounce, isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { formatNumberInputValue, restrictNonNumericCharacters, tryParseJSONObject } from '../../../../shared/utils';
import { getAutoFillLookupValues, getLookupOptions, controlValidate } from '../../../form/utils';
import { setUserInputOnCtrl, setAutoFillValues } from '../../../form/reducer/form';
import { FormContext } from '../../../customise/contexts/form-context';
import { ControlPropType, formDesignProps } from '../../ctrl_proptype';
import { choiceFields } from '../../../../shared/form-lib';

import {
  CheckboxFieldsWrapper,
  CheckboxInputLabel,
  CheckboxField,
  CheckboxInput,
  Checkbox,
} from '../checkboxes/checkboxes.styled';

import {
  RadioFieldsWrapper,
  MainChoiceWrapper,
  RadioWrapper,
  RadioLabel,
  RadioInput,
  Input,
} from '../../common-field-styles';

const LookupSubcomponent = ({ setCtrlValidationError, formDesign, ctrl }) => {
  const { inForm, inBuilder, currentPageNo } = useContext(FormContext);
  const { userCustomTexts } = useSelector((state) => state.form);
  const dispatch = useDispatch();

  const shouldFetchOptions = inForm && choiceFields.includes(ctrl.props.field_type);

  const [checkedOptions, setCheckedOptions] = useState(inForm ? ctrl.value ?? [] : []);
  const [isFetchingOptions, setIsFetchingOptions] = useState(shouldFetchOptions);
  const [textValue, setTextValue] = useState(inForm ? ctrl.value ?? '' : '');
  const [options, setOptions] = useState(inForm ? [] : ctrl.props.options);
  const [newCtrl, setNewCtrl] = useState(ctrl);

  useEffect(() => {
    const fetchData = async () => {
      if (!shouldFetchOptions) return;

      try {
        const { response, error } = await getLookupOptions(ctrl);

        if (response) setOptions(response);

        if (error) setCtrlValidationError(`Error: ${error}`);
      } finally {
        setIsFetchingOptions(false);
      }
    };

    fetchData();
  }, [inForm]);

  const saveUpdatedCtrlValue = useRef(
    debounce((newValue) => {
      dispatch(
        setUserInputOnCtrl({
          ctrlId: ctrl.internal_id,
          page: currentPageNo - 1,
          property: 'value',
          isValid: true,
          newValue,
        })
      );
    }, 200)
  );

  const displayOptions = (isDropdown = false) => {
    return options.map((option) => {
      const newOption = { value: option, key: option, text: option };
      const isOptionJSON = tryParseJSONObject(option);

      if (isOptionJSON) {
        const address = Object.values(isOptionJSON).join(', ');
        newOption.text = address;
      }

      if (isDropdown) {
        return newOption;
      } else {
        return isOptionJSON ? Object.values(isOptionJSON).join(', ') : option;
      }
    });
  };

  const handleDropdownChange = async (e, { value: newValue }) => {
    saveUpdatedCtrlValue.current(newValue);
    await startAutofillLookup(newValue);
  };

  const handleCheckboxChange = async (option) => {
    setCheckedOptions((prevCheckedOptions) => {
      const updatedCheckedOptions = prevCheckedOptions.includes(option)
        ? prevCheckedOptions.filter((item) => item !== option)
        : [...prevCheckedOptions, option];

      saveUpdatedCtrlValue.current(updatedCheckedOptions);
      startAutofillLookup(updatedCheckedOptions);
      return updatedCheckedOptions;
    });
  };

  const handleTextInputChange = (e) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    setNewCtrl((prevCtrl) => ({ ...prevCtrl, value: newValue }));
    if (ctrl.value !== newValue) {
      saveUpdatedCtrlValue.current(newValue);
    }
  };

  const handleInputBlur = async () => {
    await startAutofillLookup(textValue);
  };
  
  const handlePaste = (e) => formatNumberInputValue(e, handleTextInputChange);
  
  const handleRadioChange = async (e) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    saveUpdatedCtrlValue.current(newValue);
    await startAutofillLookup(newValue);
  };

  const startAutofillLookup = async (value) => {
    if (!inForm) return;

    const sourceFields = ctrl.props.source_field_ids.map((field) => field.split(':').pop());
    const query = value.length ? value : '';

    if (ctrl.type === 'lookup' && ctrl.props.lookup_type === 1) {
      // Validate input value for email field type
      if (ctrl.props.field_type === 'email') {
        controlValidate(newCtrl, userCustomTexts, setCtrlValidationError, false);
      }

      if (ctrl.props.form && query && query.length !== 0) {
        const { autoFillValues, error } = await getAutoFillLookupValues({ sourceFields, query, ctrl });

        if (autoFillValues && !isEmpty(autoFillValues)) {
          dispatch(setAutoFillValues({ autoFillValues }));
        }

        if (error) {
          setCtrlValidationError(`Error: ${error}`);
        }
      }
    }
  };

  if (isFetchingOptions) return <Loader inline="centered" size="tiny" active />;

  switch (ctrl.props.field_type) {
    case 'select_multiple':
    case 'select_basic':
      return (
        <MainChoiceWrapper notInBuilder={!inBuilder}>
          <Dropdown
            value={
              Array.isArray(ctrl.value)
                ? ctrl.value
                : ctrl.props.field_type === 'select_multiple'
                ? []
                : ctrl.value ?? ''
            }
            placeholder={userCustomTexts?.select_option || 'Select Option'}
            multiple={ctrl.props.field_type === 'select_multiple'}
            disabled={ctrl.props.readonly || !inForm}
            onChange={handleDropdownChange}
            id={`fpl-${ctrl.internal_id}`}
            required={ctrl.props.required}
            options={displayOptions(true)}
            style={{ marginTop: 0 }}
            selectOnBlur={false}
            selection
            search
          />
        </MainChoiceWrapper>
      );

    case 'checkboxes':
      return (
        <CheckboxFieldsWrapper className={ctrl.props.inline ? 'inline' : ''}>
          {displayOptions().map((option, index) => (
            <CheckboxField id={`fpl-${ctrl.internal_id}`} key={(option, index)}>
              <Checkbox id={`fpl-${ctrl.internal_id}`}>
                <CheckboxInput
                  className={ctrl._invalid && 'form-input-invalid'}
                  onChange={() => handleCheckboxChange(option)}
                  disabled={ctrl.props.readonly || !inForm}
                  id={`${index}-${ctrl.internal_id}`}
                  required={ctrl.props.required}
                  name={ctrl._name}
                  type="checkbox"
                />

                <CheckboxInputLabel
                  className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}
                  checked={checkedOptions.includes(option)}
                  htmlFor={`${index}-${ctrl.internal_id}`}
                  notInBuilder={!inBuilder}>
                  {option}
                </CheckboxInputLabel>
              </Checkbox>
            </CheckboxField>
          ))}
        </CheckboxFieldsWrapper>
      );

    case 'radio_buttons':
      return (
        <RadioFieldsWrapper className={ctrl.props.inline ? 'inline' : ''}>
          {displayOptions().map((option, index) => (
            <RadioWrapper key={(option, index)} id={`fpl-${ctrl.internal_id}`}>
              <RadioInput
                disabled={ctrl.props.readonly || !inForm}
                id={`${index}-${ctrl.internal_id}`}
                checked={option === textValue}
                required={ctrl.props.required}
                onChange={handleRadioChange}
                value={option}
                name={option}
                type="radio"
              />

              <RadioLabel
                className={formDesign?.design.form_direction === 'rtl' ? 'rtl' : ''}
                htmlFor={`${index}-${ctrl.internal_id}`}
                notInBuilder={!inBuilder}>
                {option}
              </RadioLabel>
            </RadioWrapper>
          ))}
        </RadioFieldsWrapper>
      );

    case 'number_input':
    case 'text_input':
    case 'email':
      return (
        <>
          <Input
            type={
              ctrl.props.field_type === 'email' ? 'email' : ctrl.props.field_type === 'number_input' ? 'number' : 'text'
            }
            placeholder={
              ctrl.props.placeholder ||
              (ctrl.props.field_type === 'email' ? 'johndoe@sample.com' : 'Type your answer here')
            }
            pattern={ctrl.props.field_type === 'number_input' ? '[0-9]+(.[0-9]+)?' : undefined}
            inputMode={ctrl.props.field_type === 'number_input' ? 'numeric' : undefined}
            disabled={ctrl.props.readonly || !inForm}
            onKeyDown={restrictNonNumericCharacters}
            onChange={handleTextInputChange}
            required={ctrl.props.required}
            readonly={ctrl.props.readonly}
            id={`fpl-${ctrl.internal_id}`}
            notInBuilder={!inBuilder}
            onBlur={handleInputBlur}
            onPaste={handlePaste}
            value={textValue}
            name={ctrl._name}
          />
        </>
      );

    default:
      return null;
  }
};

LookupSubcomponent.defaultProps = {
  setCtrlValidationError: () => null,
  formDesign: null,
};

LookupSubcomponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape(formDesignProps),
  setCtrlValidationError: PropTypes.func,
};

export default LookupSubcomponent;
